import { useEffect, useState, useCallback } from 'react';

/**
 * Chatbot SDK 就绪即视为可用。
 */
function isReady(): boolean {
  if (typeof window === 'undefined') return false;
  return 'ChatBot' in window;
}

/**
 * 监听 ChatbotGrayGate 注入的 Chatbot SDK 是否已就绪。
 *
 * 用于 spot/futures TradeFooter 的 Help 按钮等"宿主自定义入口"场景：
 * SDK 由 ChatbotGrayGate 异步注入，从注入到 `window.ChatBot` 可用之间
 * 有几百毫秒空窗期，期间调用 `ChatBot.open()` 会静默失败。
 * 在 hook 返回 false 期间应禁用 Help 入口，避免用户点击得不到响应。
 *
 * 实现：挂载后立即检查；未就绪则每 500ms 轮询，持续检测直到就绪。
 * 不设超时上限 —— SDK 注入由 ChatbotGrayGate 控制，脚本加载时间
 * 因网络环境差异较大（dev/CDN/弱网），硬超时会导致按钮永久不可用。
 */
export function useCustomerServiceReady(): boolean {
  const [ready, setReady] = useState<boolean>(() => isReady());

  const check = useCallback(() => {
    if (isReady() && !ready) setReady(true);
  }, [ready]);

  useEffect(() => {
    if (ready) return;
    if (typeof window === 'undefined') return;

    const timer = window.setInterval(check, 500);
    return () => window.clearInterval(timer);
  }, [ready, check]);

  return ready;
}
